import React, { useState, useRef } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';

const EmailChipInput = ({ emails = [], onChange, placeholder = 'Enter email addresses' }) => {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            e.preventDefault();
            addEmail();
        } else if (e.key === 'Backspace' && inputValue === '' && emails.length > 0) {
            // Remove last email if backspace pressed on empty input
            removeEmail(emails.length - 1);
        }
    };

    const handleBlur = () => {
        if (inputValue.trim()) {
            addEmail();
        }
    };

    const addEmail = () => {
        const email = inputValue.trim();
        if (!email) return;
        
        if (!validateEmail(email)) {
            toast.error(`Invalid email address: ${email}`);
            return;
        }
        
        if (emails.includes(email)) {
            toast.warning(`Email already added: ${email}`);
            return;
        }
        
        onChange([...emails, email]);
        setInputValue('');
    };

    const removeEmail = (index) => {
        const newEmails = emails.filter((_, i) => i !== index);
        onChange(newEmails);
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const pastedEmails = pastedText
            .split(/[,;\s]+/)
            .map(email => email.trim())
            .filter(email => email && validateEmail(email));
        
        const uniqueNewEmails = pastedEmails.filter(email => !emails.includes(email));
        if (uniqueNewEmails.length > 0) {
            onChange([...emails, ...uniqueNewEmails]);
        }
    };

    return (
        <div
            className="flex flex-wrap gap-2 p-2 border border-slate-200 rounded-lg bg-white min-h-[42px] cursor-text"
            onClick={() => inputRef.current?.focus()}
        >
            {emails.map((email, index) => (
                <div
                    key={index}
                    className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm"
                >
                    <span>{email}</span>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            removeEmail(index);
                        }}
                        className="hover:bg-blue-200 rounded-full p-0.5"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </div>
            ))}
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onPaste={handlePaste}
                placeholder={emails.length === 0 ? placeholder : ''}
                className="flex-1 min-w-[120px] outline-none text-sm"
            />
        </div>
    );
};

export default EmailChipInput;
